import React from "react";
import "./dual-range-slider.css";

function interpolateSpectrumColor(ratio) {
  const start = { r: 0, g: 174, b: 239 }; // #00aeef
  const end = { r: 236, g: 0, b: 140 };   // #ec008c
  const clamped = Math.max(0, Math.min(1, ratio));
  const r = Math.round(start.r + (end.r - start.r) * clamped);
  const g = Math.round(start.g + (end.g - start.g) * clamped);
  const b = Math.round(start.b + (end.b - start.b) * clamped);
  return `rgb(${r}, ${g}, ${b})`;
}

export default function DualRangeSlider({
  label,
  leftLabel,
  rightLabel,
  minValue = 2,
  maxValue = 4,
  onMinChange,
  onMaxChange,
  min = 1,
  max = 7,
  disabled = false,
  irrelevantLabel = "Irrelevant",
  irrelevant = false,
  onIrrelevantToggle,
  showOptOut = true,
  showTicks = true,
  accentColor = "",
  compact = false,
}) {
  const handleMinChange = (e) => {
    const newMin = parseInt(e.target.value, 10);
    onMinChange?.(Math.min(newMin, maxValue));
  };

  const handleMaxChange = (e) => {
    const newMax = parseInt(e.target.value, 10);
    onMaxChange?.(Math.max(newMax, minValue));
  };

  const rangeLeft = ((minValue - min) / (max - min)) * 100;
  const rangeWidth = ((maxValue - minValue) / (max - min)) * 100;
  const minRatio = (minValue - min) / (max - min);
  const maxRatio = (maxValue - min) / (max - min);
  const minColor = interpolateSpectrumColor(minRatio);
  const maxColor = interpolateSpectrumColor(maxRatio);
  const highlightBackground = `linear-gradient(90deg, ${minColor} 0%, ${maxColor} 100%)`;
  // Native <input type="range"> insets each thumb so it stays inside the
  // input's bounds: thumb center sits at `10px + ratio * (track_width −
  // 20px)`, where 20px is the thumb width. The highlight pill, by contrast,
  // uses raw percentages of the wrapper width, so its left edge runs ahead
  // of the min thumb and its right edge runs past the max thumb. Adding the
  // same inset compensation in pixels (`(1 − 2 * ratio) * 10`) realigns
  // both edges with the thumb centers so the colored bar starts and ends
  // cleanly at the circles instead of bleeding past them.
  const minThumbCompPx = (1 - 2 * minRatio) * 10;
  const maxThumbCompPx = (1 - 2 * maxRatio) * 10;
  const highlightLeft = `calc(${rangeLeft}% + ${minThumbCompPx}px)`;
  const highlightWidth = `calc(${rangeWidth}% + ${maxThumbCompPx - minThumbCompPx}px)`;

  return (
    <div className="dual-slider">
      <div className="dual-slider__header">
        <h4 className="dual-slider__label">{label}</h4>
        
        {showOptOut && onIrrelevantToggle && (
          <label className="dual-slider__optout">
            <input
              type="checkbox"
              checked={irrelevant}
              onChange={onIrrelevantToggle}
              className="dual-slider__checkbox"
            />
            <span>{irrelevantLabel}</span>
          </label>
        )}
      </div>

      <div className={`dual-slider__content ${irrelevant ? "dual-slider__content--disabled" : ""} ${compact ? "dual-slider__content--compact" : ""}`}>
        {!compact && (
          <span className="dual-slider__side dual-slider__side--left">
            {leftLabel}
          </span>
        )}

        <div className="dual-slider__track-container">
          {compact && (
            <div className="dual-slider__labels-compact">
              <span className="dual-slider__label-compact">{leftLabel}</span>
              <span className="dual-slider__label-compact">{rightLabel}</span>
            </div>
          )}

          <div className="dual-slider__wrapper">
            <div className="dual-slider__track-bg" />

            <div
              className="dual-slider__track-highlight"
              style={{
                left: highlightLeft,
                width: highlightWidth,
                background: accentColor || highlightBackground
              }}
            />

            <input
              type="range"
              min={min}
              max={max}
              value={minValue}
              onChange={handleMinChange}
              disabled={disabled || irrelevant}
              className="dual-slider__input dual-slider__input--min"
              style={{ "--dual-slider-thumb": minColor }}
              aria-label={label ? `${label} minimum` : "Minimum value"}
              aria-valuetext={String(minValue)}
            />

            <input
              type="range"
              min={min}
              max={max}
              value={maxValue}
              onChange={handleMaxChange}
              disabled={disabled || irrelevant}
              className="dual-slider__input dual-slider__input--max"
              style={{ "--dual-slider-thumb": maxColor }}
              aria-label={label ? `${label} maximum` : "Maximum value"}
              aria-valuetext={String(maxValue)}
            />
          </div>

          {showTicks && (
            <div className="dual-slider__ticks">
              {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
                <span key={n} className="dual-slider__tick">{n}</span>
              ))}
            </div>
          )}
        </div>

        {!compact && (
          <span className="dual-slider__side dual-slider__side--right">
            {rightLabel}
          </span>
        )}
      </div>
    </div>
  );
}
